import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { FC, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import RebookingLockup from 'src/assets/png/rebookers-pricing-released-lock-up.png';
import RebookingBasketball from 'src/assets/png/rebookers-large.png';
import RebookingHos from 'src/features/rebooking/assets/small-header.png';

import RebookersHosLockup from '../../assets/hos_rebooking_2728_white.png';
import { Button } from '../../../../components/button';
import { createTransactionsLink } from '../../utils/create-transactions-link';
import { getTheme } from '../../../../utils/get-theme';

type RebookersKeepItLinkProps = {
    roomCode: string;
    currency: string;
    price: number;
    rebookingOpen: boolean;
    rebookingOpenDate: Date;
    isPromotionalOffer: boolean;
    isAffordableRoom: boolean;
    weeksOff: number;
    keepItIncentiveValue: number | null;
};

export const RebookersKeepItLink: FC<RebookersKeepItLinkProps> = ({
    roomCode,
    currency,
    price,
    rebookingOpen,
    rebookingOpenDate,
    isPromotionalOffer,
    weeksOff,
    isAffordableRoom,
    keepItIncentiveValue,
}) => {
    const { t } = useTranslation();
    const { palette, spacing } = useTheme();
    const { isHosTheme } = getTheme();
    const currencySymbol = currency.toLowerCase() === 'eur' ? '€' : '£';
    const isMonthly = currency.toLowerCase() === 'eur';

    const url = createTransactionsLink({ room_code: roomCode });

    const date = rebookingOpenDate.getDate();
    const month = rebookingOpenDate.getMonth() + 1;
    const year = rebookingOpenDate.getFullYear();

    const formattedDate = `${date}-${month}-${year}`;

    const cashbackIncentive = !!keepItIncentiveValue && (
        <Fragment>
            <Box textAlign="center">
                <Typography variant="h2" color="white">
                    +
                </Typography>
            </Box>
            <Box textAlign="center" whiteSpace="nowrap" textTransform="uppercase">
                <Typography
                    mt={1}
                    variant="h2"
                    color={palette.common.white}
                    display="inline-block"
                    noWrap
                >
                    {`${currencySymbol}${keepItIncentiveValue} ${t('rebooking_keep_it_card_promotion_cashback')}`}
                </Typography>
            </Box>
        </Fragment>
    );

    return (
        <Grid
            container
            alignItems="stretch"
            bgcolor={isHosTheme ? palette.secondary.dark : 'black'}
            justifyContent="space-between"
        >
            <Grid
                item
                xs={12}
                lg={6}
                p={{ xs: 4, lg: 8 }}
                bgcolor={isHosTheme ? palette.secondary.dark : 'black'}
            >
                <Box css={{ gridTemplateRows: '2fr 1fr' }} width={{ xs: '100%', lg: '60%' }}>
                    <Box pb={4}>
                        <img
                            alt="Rebookers Info"
                            src={isHosTheme ? RebookersHosLockup : RebookingLockup}
                            css={{
                                marginBottom: spacing(2),
                                ...(isHosTheme && { marginLeft: spacing(-3) }),
                            }}
                        />
                        <Box {...(isPromotionalOffer && { mt: 2, mb: 1 })} width="fit-content">
                            {isPromotionalOffer ? (
                                <Stack spacing={1}>
                                    <Box>
                                        <Typography
                                            variant="h2"
                                            display="inline-block"
                                            color={palette.grey[500]}
                                        >
                                            <em
                                                {...(isHosTheme && {
                                                    style: { color: palette.grey[50] },
                                                })}
                                            >
                                                {t(
                                                    'rebooking_keep_it_card_promotion_guarantee_intro'
                                                )}{' '}
                                                <Box
                                                    display="inline-block"
                                                    color={palette.primary.main}
                                                >
                                                    {`${currencySymbol}${price}`}
                                                </Box>{' '}
                                                /{' '}
                                                {isMonthly
                                                    ? t(
                                                          'rebooking_keep_it_card_cta_price_period_month'
                                                      )
                                                    : t(
                                                          'rebooking_keep_it_card_cta_price_period_week'
                                                      )}
                                            </em>
                                        </Typography>
                                    </Box>

                                    {!isAffordableRoom && (
                                        <Fragment>
                                            <Box textAlign="center">
                                                <Typography variant="h2" color="white">
                                                    +
                                                </Typography>
                                            </Box>
                                            <Box whiteSpace="nowrap" textTransform="uppercase">
                                                <Typography
                                                    variant="h1"
                                                    color={palette.primary.main}
                                                    display="inline-block"
                                                    noWrap
                                                >
                                                    <Trans
                                                        i18nKey="rebooking_keep_it_card_promotion_guarantee"
                                                        values={{ count: weeksOff }}
                                                    />
                                                </Typography>
                                            </Box>
                                            {cashbackIncentive}
                                        </Fragment>
                                    )}
                                </Stack>
                            ) : (
                                <Fragment>
                                    <Box whiteSpace="nowrap">
                                        <Typography
                                            variant="h1"
                                            color={palette.primary.main}
                                            display="inline-block"
                                            noWrap
                                        >
                                            {`${currencySymbol}${price}`}
                                        </Typography>{' '}
                                        <Typography
                                            variant="h2"
                                            display="inline-block"
                                            color="white"
                                            noWrap
                                            textTransform="uppercase"
                                        >
                                            /{' '}
                                            <b>
                                                {isMonthly
                                                    ? t(
                                                          'rebooking_keep_it_card_cta_price_period_month'
                                                      )
                                                    : t(
                                                          'rebooking_keep_it_card_cta_price_period_week'
                                                      )}
                                            </b>
                                        </Typography>
                                    </Box>

                                    <Box display="inline-block">
                                        <Typography
                                            variant="h6"
                                            display="inline-block"
                                            color={palette.grey[500]}
                                        >
                                            <em>{t('rebooking_keep_it_card_price_guarantee')}</em>
                                        </Typography>
                                    </Box>
                                    {cashbackIncentive}
                                </Fragment>
                            )}
                        </Box>
                    </Box>
                </Box>
                <Button design="primary" href={url} target="_blank" disabled={!rebookingOpen}>
                    {rebookingOpen ? (
                        t('rebooking_keep_it_card_cta')
                    ) : (
                        <Trans
                            i18nKey="rebooking_keep_it_card_cta_price_released"
                            values={{ date: formattedDate }}
                        />
                    )}
                </Button>
                <Box py={2}>
                    <Typography color={palette.grey[50]} fontWeight={500}>
                        {t('rebooking_keep_it_card_room_availability_disclaimer')}
                    </Typography>
                    <Typography color={isHosTheme ? palette.primary.light : palette.grey[500]}>
                        {isPromotionalOffer
                            ? t(
                                  'rebooking_keep_it_card_room_availability_disclaimer_subtitle_rebooker'
                              )
                            : t('rebooking_keep_it_card_room_availability_disclaimer_subtitle')}
                    </Typography>
                </Box>
            </Grid>
            <Grid
                alignSelf="stretch"
                item
                lg={6}
                width="100%"
                flexGrow={1}
                display={{
                    xs: 'none',
                    lg: 'inherit',
                }}
                css={{
                    backgroundImage: `linear-gradient(90deg, ${isHosTheme ? 'rgba(19, 34, 39, 1)' : 'rgba(0, 0, 0, 1)'} 0%, ${isHosTheme ? 'rgba(19, 34, 39, 0)' : 'rgba(0, 0, 0, 0)'} 40%, ${isHosTheme ? 'rgba(19, 34, 39, 0)' : 'rgba(0, 0, 0, 0)'} 100%), url(${isHosTheme ? RebookingHos : RebookingBasketball})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                }}
            />
        </Grid>
    );
};
